/**
 * Admin project management page.
 *
 * Lists all projects across all users, lets admins update statuses,
 * upload platform-specific deliverables, and set deployed website URLs.
 * Also provides quick links to blog and translation management.
 *
 * @module AdminPage
 */

import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid2 as Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
  Alert,
  alpha,
  Badge,
  Collapse,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import TranslateIcon from '@mui/icons-material/Translate';
import ArticleIcon from '@mui/icons-material/Article';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CollectionsIcon from '@mui/icons-material/Collections';
import DescriptionIcon from '@mui/icons-material/Description';
import DrawIcon from '@mui/icons-material/Draw';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import MarkdownPreview from '../components/MarkdownPreview';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import ProjectProgressBar, { STATUSES } from '../components/ProjectProgressBar';
import { GREEN, DARK_BLUE } from '../theme';
import { PageTransition, FloatingOrb } from '../components/AnimatedComponents';

interface Deliverable {
  platform: string;
  filename: string;
  blob_name: string;
  uploaded_at: string;
}

interface Message {
  id: string;
  project_id: string;
  sender_id: string;
  sender_role: string;
  content: string;
  created_at: string;
  read_by_admin: boolean;
  read_by_client: boolean;
}

interface QuoteData {
  summary: string;
  contract_text: string;
  total_price: number | null;
  currency: string;
  reference_number: string | null;
  generated_at: string | null;
  admin_signature: boolean;
  admin_signed_at: string | null;
  client_signature: boolean;
  client_signed_at: string | null;
  pdf_blob_name: string | null;
}

interface Project {
  id: string;
  user_id: string;
  project_type: string;
  app_name: string;
  description: string;
  target_audience?: string;
  key_features?: string[] | string;
  ai_features?: string[] | string;
  design_preferences?: string[] | string;
  logo_blob_name?: string;
  timeline?: string;
  deadline?: string;
  budget_range?: string;
  additional_notes?: string;
  status: string;
  created_at: string;
  updated_at: string;
  deliverables: Deliverable[];
  website_url?: string;
  website_url_public?: boolean;
  preview_token?: string;
  unread_client?: number;
  unread_admin?: number;
  quote?: QuoteData | null;
  archived?: boolean;
}

export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const statusTabKeys: Record<string, string> = {
    submitted: 'admin.tabSubmitted',
    accepted: 'admin.tabAccepted',
    quote_sent: 'admin.tabQuoteSent',
    contract_signed: 'admin.tabContractSigned',
    poc: 'admin.tabPoc',
    refine: 'admin.tabRefine',
    pre_delivered: 'admin.tabPredelivered',
    finished: 'admin.tabFinished',
  };

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [uploadDialog, setUploadDialog] = useState<{
    open: boolean;
    projectId: string;
    platform: string;
  }>({ open: false, projectId: '', platform: '' });
  const [urlDialog, setUrlDialog] = useState<{
    open: boolean;
    projectId: string;
    url: string;
  }>({ open: false, projectId: '', url: '' });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [expandedMessagesId, setExpandedMessagesId] = useState<string | null>(null);
  const [projectMessages, setProjectMessages] = useState<Record<string, Message[]>>({});
  const [messageInput, setMessageInput] = useState<Record<string, string>>({});
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [generatingQuote, setGeneratingQuote] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState<string | null>(null);
  const [quoteEditData, setQuoteEditData] = useState<{
    summary: string;
    contract_text: string;
    total_price: number;
    currency: string;
  }>({ summary: '', contract_text: '', total_price: 0, currency: 'EUR' });
  const [signingQuoteId, setSigningQuoteId] = useState<string | null>(null);
  const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState<{ open: boolean; projectId: string; appName: string }>({ open: false, projectId: '', appName: '' });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; projectId: string; appName: string }>({ open: false, projectId: '', appName: '' });

  const totalUnread = projects.reduce((sum, p) => sum + (p.unread_admin || 0), 0);

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/login');
      return;
    }
    fetchProjects();
  }, [user, isAdmin, navigate]);

  const fetchProjects = (archived?: boolean) => {
    const includeArchived = archived !== undefined ? archived : showArchived;
    api
      .get('/api/projects/', { params: { include_archived: includeArchived } })
      .then((res) => setProjects(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleStatusChange = async (projectId: string, newStatus: string) => {
    try {
      await api.patch(`/api/projects/${projectId}/status`, { status: newStatus });
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, status: newStatus } : p))
      );
      setMessage({ type: 'success', text: t('admin.statusUpdated') });
    } catch {
      setMessage({ type: 'error', text: t('admin.statusUpdateFailed') });
    }
  };

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    formData.append('platform', uploadDialog.platform);
    try {
      await api.post(`/api/projects/${uploadDialog.projectId}/deliverables`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadDialog({ open: false, projectId: '', platform: '' });
      setMessage({ type: 'success', text: t('admin.fileUploaded') });
      fetchProjects();
    } catch {
      setMessage({ type: 'error', text: t('admin.uploadFailed') });
    }
  };

  const handleUrlUpdate = async () => {
    try {
      await api.patch(`/api/projects/${urlDialog.projectId}/website-url`, {
        website_url: urlDialog.url,
      });
      setProjects((prev) =>
        prev.map((p) =>
          p.id === urlDialog.projectId ? { ...p, website_url: urlDialog.url } : p
        )
      );
      setUrlDialog({ open: false, projectId: '', url: '' });
      setMessage({ type: 'success', text: t('admin.urlUpdated') });
    } catch {
      setMessage({ type: 'error', text: t('admin.urlUpdateFailed') });
    }
  };

  const handleTogglePublic = async (projectId: string, isPublic: boolean) => {
    try {
      await api.patch(`/api/projects/${projectId}/website-url-public`, {
        public: isPublic,
      });
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, website_url_public: isPublic } : p))
      );
      setMessage({ type: 'success', text: t('admin.urlUpdated') });
    } catch {
      setMessage({ type: 'error', text: t('admin.urlUpdateFailed') });
    }
  };

  const loadMessages = async (projectId: string) => {
    setLoadingMessages(true);
    try {
      const res = await api.get(`/api/projects/${projectId}/messages`);
      setProjectMessages((prev) => ({ ...prev, [projectId]: res.data }));
      await api.post(`/api/projects/${projectId}/messages/mark-read`);
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, unread_admin: 0 } : p))
      );
    } catch {
      // ignore
    } finally {
      setLoadingMessages(false);
    }
  };

  const toggleMessages = async (projectId: string) => {
    if (expandedMessagesId === projectId) {
      setExpandedMessagesId(null);
    } else {
      setExpandedMessagesId(projectId);
      await loadMessages(projectId);
    }
  };

  const sendAdminMessage = async (projectId: string) => {
    const content = messageInput[projectId]?.trim();
    if (!content) return;
    setSendingMessage(true);
    try {
      await api.post(`/api/projects/${projectId}/messages`, { content });
      setMessageInput((prev) => ({ ...prev, [projectId]: '' }));
      await loadMessages(projectId);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {
      setMessage({ type: 'error', text: t('admin.messageFailed') });
    } finally {
      setSendingMessage(false);
    }
  };

  const handleGenerateQuote = async (projectId: string) => {
    setGeneratingQuote(true);
    try {
      const res = await api.post(`/api/projects/${projectId}/quote/generate`);
      setProjects((prev) => prev.map((p) => (p.id === projectId ? res.data : p)));
      setMessage({ type: 'success', text: t('admin.quoteGenerated') });
    } catch {
      setMessage({ type: 'error', text: t('admin.quoteGenerateFailed') });
    } finally {
      setGeneratingQuote(false);
    }
  };

  const startEditQuote = (project: Project) => {
    if (project.quote) {
      setEditingQuoteId(project.id);
      setQuoteEditData({
        summary: project.quote.summary,
        contract_text: project.quote.contract_text,
        total_price: project.quote.total_price || 0,
        currency: project.quote.currency,
      });
    }
  };

  const handleSaveQuote = async (projectId: string) => {
    try {
      const res = await api.put(`/api/projects/${projectId}/quote`, quoteEditData);
      setProjects((prev) => prev.map((p) => (p.id === projectId ? res.data : p)));
      setEditingQuoteId(null);
      setMessage({ type: 'success', text: t('admin.quoteSaved') });
    } catch {
      setMessage({ type: 'error', text: t('admin.quoteSaveFailed') });
    }
  };

  const initSignatureCanvas = (canvas: HTMLCanvasElement | null) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#002a3a';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  };

  const handleSignatureMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = e.currentTarget;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const handleSignatureMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = e.currentTarget;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const handleSignatureMouseUp = () => {
    setIsDrawing(false);
  };

  const handleAdminSign = async (projectId: string) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const signature = canvas.toDataURL('image/png');
    try {
      const res = await api.post(`/api/projects/${projectId}/quote/sign-admin`, { signature });
      setProjects((prev) => prev.map((p) => (p.id === projectId ? res.data : p)));
      setSigningQuoteId(null);
      setMessage({ type: 'success', text: t('admin.quoteSigned') });
    } catch {
      setMessage({ type: 'error', text: t('admin.quoteSignFailed') });
    }
  };

  const handleArchive = async (projectId: string) => {
    try {
      await api.patch(`/api/projects/${projectId}/archive`);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      setMessage({ type: 'success', text: t('admin.projectArchived') });
    } catch {
      setMessage({ type: 'error', text: t('admin.archiveFailed') });
    } finally {
      setArchiveDialog({ open: false, projectId: '', appName: '' });
    }
  };

  const handleRestore = async (projectId: string) => {
    try {
      const res = await api.patch(`/api/projects/${projectId}/restore`);
      setProjects((prev) => prev.map((p) => (p.id === projectId ? res.data : p)));
      setMessage({ type: 'success', text: t('admin.projectRestored') });
    } catch {
      setMessage({ type: 'error', text: t('admin.restoreFailed') });
    }
  };

  const handleDelete = async (projectId: string) => {
    try {
      await api.delete(`/api/projects/${projectId}`);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      setMessage({ type: 'success', text: t('admin.projectDeleted') });
    } catch {
      setMessage({ type: 'error', text: t('admin.deleteFailed') });
    } finally {
      setDeleteDialog({ open: false, projectId: '', appName: '' });
    }
  };

  const toggleShowArchived = () => {
    const newVal = !showArchived;
    setShowArchived(newVal);
    setLoading(true);
    fetchProjects(newVal);
  };

  const handleDownloadContract = async (projectId: string) => {
    try {
      const res = await api.get(`/api/projects/${projectId}/quote/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contract.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setMessage({ type: 'error', text: t('admin.contractDownloadFailed') });
    }
  };

  const filteredProjects =
    tabValue === 0
      ? projects
      : projects.filter((p) => p.status === STATUSES[tabValue - 1]?.key);

  const platforms = ['android', 'ios', 'windows', 'macos', 'linux'];

  if (!user || !isAdmin) return null;

  return (
    <PageTransition>
      <SEO title={t('admin.seoTitle')} description={t('admin.seoDesc')} noindex />
      <Box
        sx={{
          py: { xs: 3, md: 5 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          background: `linear-gradient(180deg, ${alpha(DARK_BLUE, 0.02)} 0%, transparent 40%)`,
        }}
      >
        <FloatingOrb size={250} color={DARK_BLUE} top={-60} left={-80} animationDelay="2s" />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AdminPanelSettingsIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('admin.title')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Typography color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                {t('admin.subtitle')}
              </Typography>
              {totalUnread > 0 && (
                <Chip
                  icon={<MailOutlineIcon />}
                  label={t('admin.totalUnread', { count: totalUnread })}
                  color="error"
                  size="small"
                />
              )}
              <Button
                variant="outlined"
                startIcon={<ArticleIcon />}
                onClick={() => navigate('/admin/blog')}
                sx={{ borderRadius: 3, ml: 'auto' }}
              >
                {t('nav.blog')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CollectionsIcon />}
                onClick={() => navigate('/admin/showcase')}
                sx={{ borderRadius: 3 }}
              >
                {t('nav.showcase')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<TranslateIcon />}
                onClick={() => navigate('/admin/translations')}
                sx={{ borderRadius: 3 }}
              >
                {t('nav.translations')}
              </Button>
              <Button
                variant={showArchived ? 'contained' : 'outlined'}
                startIcon={<InventoryIcon />}
                onClick={toggleShowArchived}
                sx={{ borderRadius: 3 }}
                color={showArchived ? 'secondary' : 'primary'}
              >
                {showArchived ? t('admin.hideArchived') : t('admin.showArchived')}
              </Button>
            </Stack>
          </motion.div>

          <AnimatePresence>
            {message && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <Alert
                  severity={message.type}
                  onClose={() => setMessage(null)}
                  sx={{ mb: 3 }}
                >
                  {message.text}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mb: 3,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 2,
              },
            }}
          >
            <Tab label={t('admin.tabAll', { count: projects.length })} />
            {STATUSES.map((s) => (
              <Tab
                key={s.key}
                label={t(statusTabKeys[s.key], { count: projects.filter((p) => p.status === s.key).length })}
              />
            ))}
          </Tabs>

          {loading ? (
            <Typography>{t('admin.loading')}</Typography>
          ) : filteredProjects.length === 0 ? (
            <Typography color="text.secondary">{t('admin.noProjects')}</Typography>
          ) : (
            <Stack spacing={2}>
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: index * 0.06 }}
                >
                  <Card
                    sx={{
                      border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: `0 8px 30px ${alpha(DARK_BLUE, 0.08)}`,
                        borderColor: alpha(GREEN, 0.15),
                      },
                    }}
                  >
                    <CardContent>
                      {/* Header */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          gap: 2,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Badge
                              badgeContent={project.unread_admin || 0}
                              color="error"
                              max={99}
                            >
                              <Typography variant="h5" sx={{ fontWeight: 600 }}>{project.app_name}</Typography>
                            </Badge>
                            <IconButton
                              size="small"
                              onClick={() =>
                                setExpandedId(expandedId === project.id ? null : project.id)
                              }
                              sx={{
                                transition: 'transform 0.3s',
                                transform: expandedId === project.id ? 'rotate(180deg)' : 'rotate(0)',
                              }}
                            >
                              {expandedId === project.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                            <Chip
                              label={project.project_type}
                              size="small"
                              color="primary"
                            />
                            <Typography variant="body2" color="text.secondary">
                              {t('admin.userDate', { userId: project.user_id.substring(0, 8), date: new Date(project.created_at).toLocaleDateString() })}
                            </Typography>
                          </Stack>
                        </Box>

                        {/* Status Selector */}
                        <Stack direction="row" spacing={1} alignItems="center">
                          {project.archived && (
                            <Chip
                              icon={<ArchiveIcon />}
                              label={t('admin.archivedBadge')}
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>{t('admin.statusLabel')}</InputLabel>
                            <Select
                              value={project.status}
                              label={t('admin.statusLabel')}
                              onChange={(e) => handleStatusChange(project.id, e.target.value)}
                            >
                              {STATUSES.map((s) => (
                                <MenuItem key={s.key} value={s.key}>
                                  {t('status.' + s.key.replace('_', '-'))}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {project.archived ? (
                            <Tooltip title={t('admin.restoreProject')}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleRestore(project.id)}
                              >
                                <UnarchiveIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t('admin.archiveProject')}>
                              <IconButton
                                size="small"
                                onClick={() => setArchiveDialog({ open: true, projectId: project.id, appName: project.app_name })}
                              >
                                <ArchiveIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={t('admin.deleteProject')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteDialog({ open: true, projectId: project.id, appName: project.app_name })}
                            >
                              <DeleteForeverIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>

                      {/* Progress Bar */}
                      <Box sx={{ mt: 2, mb: 1 }}>
                        <ProjectProgressBar status={project.status} />
                      </Box>

                      {/* Expanded Details */}
                      <AnimatePresence>
                        {expandedId === project.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            style={{ overflow: 'hidden' }}
                          >
                            <Box sx={{ mt: 3 }}>
                              <Divider sx={{ mb: 2 }} />
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <Typography variant="subtitle2" color="text.secondary">{t('admin.description')}</Typography>
                                  <Typography variant="body2" sx={{ mb: 2 }}>{project.description}</Typography>

                                  {project.target_audience && (
                                    <>
                                      <Typography variant="subtitle2" color="text.secondary">{t('admin.targetAudience')}</Typography>
                                      <Typography variant="body2" sx={{ mb: 2 }}>{project.target_audience}</Typography>
                                    </>
                                  )}
                                  {project.key_features && (Array.isArray(project.key_features) ? project.key_features.length > 0 : project.key_features) && (
                                    <>
                                      <Typography variant="subtitle2" color="text.secondary">{t('admin.keyFeatures')}</Typography>
                                      {Array.isArray(project.key_features) ? (
                                        <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.5 }}>
                                          {project.key_features.map((f, i) => (
                                            <li key={i}><Typography variant="body2">{f}</Typography></li>
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography variant="body2" sx={{ mb: 2 }}>{project.key_features}</Typography>
                                      )}
                                    </>
                                  )}
                                  {project.ai_features && (Array.isArray(project.ai_features) ? project.ai_features.length > 0 : project.ai_features) && (
                                    <>
                                      <Typography variant="subtitle2" color="text.secondary">{t('admin.aiFeatures')}</Typography>
                                      {Array.isArray(project.ai_features) ? (
                                        <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.5 }}>
                                          {project.ai_features.map((f, i) => (
                                            <li key={i}><Typography variant="body2">{f}</Typography></li>
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography variant="body2" sx={{ mb: 2 }}>{project.ai_features}</Typography>
                                      )}
                                    </>
                                  )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  {project.design_preferences && (Array.isArray(project.design_preferences) ? project.design_preferences.length > 0 : project.design_preferences) && (
                                    <>
                                      <Typography variant="subtitle2" color="text.secondary">{t('admin.designPreferences')}</Typography>
                                      {Array.isArray(project.design_preferences) ? (
                                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                          {project.design_preferences.map((color, i) => (
                                            <Tooltip key={i} title={color}>
                                              <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: color, border: '1px solid rgba(0,0,0,0.12)' }} />
                                            </Tooltip>
                                          ))}
                                        </Stack>
                                      ) : (
                                        <Typography variant="body2" sx={{ mb: 2 }}>{project.design_preferences}</Typography>
                                      )}
                                    </>
                                  )}
                                  {project.deadline && (
                                    <>
                                      <Typography variant="subtitle2" color="text.secondary">{t('admin.deadline')}</Typography>
                                      <Typography variant="body2" sx={{ mb: 2 }}>{project.deadline}</Typography>
                                    </>
                                  )}
                                  {project.timeline && (
                                    <>
                                      <Typography variant="subtitle2" color="text.secondary">{t('admin.timeline')}</Typography>
                                      <Typography variant="body2" sx={{ mb: 2 }}>{project.timeline}</Typography>
                                    </>
                                  )}
                                  {project.budget_range && (
                                    <>
                                      <Typography variant="subtitle2" color="text.secondary">{t('admin.budget')}</Typography>
                                      <Typography variant="body2" sx={{ mb: 2 }}>{project.budget_range}</Typography>
                                    </>
                                  )}
                                  {project.additional_notes && (
                                    <>
                                      <Typography variant="subtitle2" color="text.secondary">{t('admin.additionalNotes')}</Typography>
                                      <Typography variant="body2" sx={{ mb: 2 }}>{project.additional_notes}</Typography>
                                    </>
                                  )}
                                </Grid>
                              </Grid>

                              {/* Quote & Contract Section */}
                              <Divider sx={{ my: 2 }} />
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <DescriptionIcon fontSize="small" />
                                  {t('admin.quoteContract')}
                                </Typography>

                                {/* Generate Quote Button */}
                                {(project.status === 'accepted' || (project.status === 'quote_sent' && !project.quote?.admin_signature)) && (
                                  <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={generatingQuote ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                                    onClick={() => handleGenerateQuote(project.id)}
                                    disabled={generatingQuote}
                                    sx={{ borderRadius: 3, mb: 2 }}
                                  >
                                    {project.quote ? t('admin.regenerateQuote') : t('admin.generateQuote')}
                                  </Button>
                                )}

                                {/* Quote Display / Edit */}
                                {project.quote && (
                                  <Box sx={{ mt: 1 }}>
                                    {editingQuoteId === project.id ? (
                                      /* Edit Mode */
                                      <Box>
                                        <TextField
                                          fullWidth
                                          multiline
                                          minRows={6}
                                          label={t('admin.quoteSummary')}
                                          value={quoteEditData.summary}
                                          onChange={(e) => setQuoteEditData((prev) => ({ ...prev, summary: e.target.value }))}
                                          sx={{ mb: 2 }}
                                        />
                                        <TextField
                                          fullWidth
                                          multiline
                                          minRows={8}
                                          label={t('admin.contractText')}
                                          value={quoteEditData.contract_text}
                                          onChange={(e) => setQuoteEditData((prev) => ({ ...prev, contract_text: e.target.value }))}
                                          sx={{ mb: 2 }}
                                        />
                                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                          <TextField
                                            label={t('admin.totalPrice')}
                                            type="number"
                                            value={quoteEditData.total_price}
                                            onChange={(e) => setQuoteEditData((prev) => ({ ...prev, total_price: parseFloat(e.target.value) || 0 }))}
                                            sx={{ width: 200 }}
                                          />
                                          <TextField
                                            label={t('admin.currency')}
                                            value={quoteEditData.currency}
                                            onChange={(e) => setQuoteEditData((prev) => ({ ...prev, currency: e.target.value }))}
                                            sx={{ width: 100 }}
                                          />
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                          <Button variant="contained" color="secondary" onClick={() => handleSaveQuote(project.id)} sx={{ borderRadius: 3 }}>
                                            {t('admin.save')}
                                          </Button>
                                          <Button onClick={() => setEditingQuoteId(null)} sx={{ borderRadius: 3 }}>
                                            {t('admin.cancel')}
                                          </Button>
                                        </Stack>
                                      </Box>
                                    ) : (
                                      /* View Mode */
                                      <Box>
                                        <Box sx={{ p: 2, bgcolor: alpha(GREEN, 0.04), borderRadius: 2, border: `1px solid ${alpha(GREEN, 0.15)}`, mb: 2 }}>
                                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                              {t('admin.quoteSummary')}
                                            </Typography>
                                            <Chip
                                              label={`${project.quote.currency} ${project.quote.total_price?.toLocaleString()}`}
                                              color="secondary"
                                              size="small"
                                              sx={{ fontWeight: 700 }}
                                            />
                                          </Stack>
                                          <MarkdownPreview content={project.quote.summary} maxHeight={200} />
                                        </Box>

                                        <Box sx={{ p: 2, bgcolor: alpha(DARK_BLUE, 0.02), borderRadius: 2, border: `1px solid ${alpha(DARK_BLUE, 0.08)}`, mb: 2 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                            {t('admin.contractText')}
                                          </Typography>
                                          <MarkdownPreview content={project.quote.contract_text} maxHeight={200} />
                                        </Box>

                                        {/* Signature Status */}
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                          <Chip
                                            label={project.quote.admin_signature ? t('admin.adminSigned') : t('admin.adminNotSigned')}
                                            color={project.quote.admin_signature ? 'success' : 'default'}
                                            size="small"
                                            variant="outlined"
                                          />
                                          <Chip
                                            label={project.quote.client_signature ? t('admin.clientSigned') : t('admin.clientNotSigned')}
                                            color={project.quote.client_signature ? 'success' : 'default'}
                                            size="small"
                                            variant="outlined"
                                          />
                                        </Stack>

                                        {/* Action Buttons */}
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                          {!project.quote.admin_signature && (
                                            <>
                                              <Button
                                                size="small"
                                                startIcon={<EditIcon />}
                                                onClick={() => startEditQuote(project)}
                                                sx={{ borderRadius: 3 }}
                                              >
                                                {t('admin.editQuote')}
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                color="secondary"
                                                startIcon={<DrawIcon />}
                                                onClick={() => {
                                                  setSigningQuoteId(project.id);
                                                  setTimeout(() => initSignatureCanvas(signatureCanvasRef.current), 100);
                                                }}
                                                sx={{ borderRadius: 3 }}
                                              >
                                                {t('admin.signQuote')}
                                              </Button>
                                            </>
                                          )}
                                          {project.quote.admin_signature && project.quote.client_signature && (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              startIcon={<PictureAsPdfIcon />}
                                              onClick={() => handleDownloadContract(project.id)}
                                              sx={{ borderRadius: 3 }}
                                            >
                                              {t('admin.downloadContract')}
                                            </Button>
                                          )}
                                        </Stack>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              {/* Actions: Upload Deliverables */}
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                {t('admin.uploadDeliverables')}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                                {platforms.map((platform) => {
                                  const existing = project.deliverables.find((d) => d.platform === platform);
                                  return (
                                    <Button
                                      key={platform}
                                      variant={existing ? 'contained' : 'outlined'}
                                      size="small"
                                      startIcon={<UploadFileIcon />}
                                      color={existing ? 'secondary' : 'primary'}
                                      onClick={() =>
                                        setUploadDialog({ open: true, projectId: project.id, platform })
                                      }
                                      sx={{ borderRadius: 3 }}
                                    >
                                      {existing ? t('admin.uploaded', { platform }) : platform}
                                    </Button>
                                  );
                                })}
                              </Stack>

                              {/* Website URL */}
                              {(project.project_type === 'website' || project.project_type === 'both') && (
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                    {t('admin.websiteUrl')}
                                  </Typography>
                                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    {project.website_url && (
                                      <>
                                        <Chip
                                          label={project.website_url}
                                          color="primary"
                                          variant="outlined"
                                          size="small"
                                        />
                                        <Tooltip title={project.website_url_public ? t('admin.makePrivate') : t('admin.makePublic')}>
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                checked={project.website_url_public || false}
                                                onChange={(e) => handleTogglePublic(project.id, e.target.checked)}
                                                size="small"
                                              />
                                            }
                                            label={
                                              <Chip
                                                icon={project.website_url_public ? <PublicIcon /> : <LockIcon />}
                                                label={project.website_url_public ? t('admin.publicBadge') : t('admin.makePrivate')}
                                                size="small"
                                                color={project.website_url_public ? 'success' : 'default'}
                                                variant="outlined"
                                              />
                                            }
                                            sx={{ ml: 0 }}
                                          />
                                        </Tooltip>
                                      </>
                                    )}
                                    <Button
                                      size="small"
                                      startIcon={<LinkIcon />}
                                      onClick={() =>
                                        setUrlDialog({
                                          open: true,
                                          projectId: project.id,
                                          url: project.website_url || '',
                                        })
                                      }
                                      sx={{ borderRadius: 3 }}
                                    >
                                      {project.website_url ? t('admin.updateUrl') : t('admin.setUrl')}
                                    </Button>
                                  </Stack>
                                </Box>
                              )}

                              {/* Messages */}
                              <Divider sx={{ my: 2 }} />
                              <Box>
                                <Button
                                  onClick={() => toggleMessages(project.id)}
                                  startIcon={
                                    <Badge badgeContent={project.unread_admin || 0} color="error" max={99}>
                                      <ChatBubbleOutlineIcon />
                                    </Badge>
                                  }
                                  sx={{ borderRadius: 3, textTransform: 'none', mb: 1 }}
                                >
                                  {t('admin.messages')}
                                  {(project.unread_admin || 0) > 0 && (
                                    <Chip
                                      label={t('admin.unreadMessages', { count: project.unread_admin })}
                                      size="small"
                                      color="error"
                                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Button>

                                <Collapse in={expandedMessagesId === project.id}>
                                  <Box
                                    sx={{
                                      p: 2,
                                      bgcolor: alpha(DARK_BLUE, 0.02),
                                      borderRadius: 2,
                                      border: `1px solid ${alpha(DARK_BLUE, 0.08)}`,
                                    }}
                                  >
                                    {loadingMessages ? (
                                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                        <CircularProgress size={24} />
                                      </Box>
                                    ) : (
                                      <>
                                        <Box
                                          sx={{
                                            maxHeight: 300,
                                            overflowY: 'auto',
                                            mb: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                          }}
                                        >
                                          {(projectMessages[project.id] || []).length === 0 ? (
                                            <Typography
                                              color="text.secondary"
                                              sx={{ textAlign: 'center', py: 3, fontSize: '0.9rem' }}
                                            >
                                              {t('admin.noMessages')}
                                            </Typography>
                                          ) : (
                                            (projectMessages[project.id] || []).map((msg) => (
                                              <Box
                                                key={msg.id}
                                                sx={{
                                                  alignSelf: msg.sender_role === 'admin' ? 'flex-end' : 'flex-start',
                                                  maxWidth: '75%',
                                                  p: 1.5,
                                                  borderRadius: 2,
                                                  bgcolor: msg.sender_role === 'admin'
                                                    ? alpha(GREEN, 0.1)
                                                    : alpha(DARK_BLUE, 0.06),
                                                  border: `1px solid ${msg.sender_role === 'admin'
                                                    ? alpha(GREEN, 0.15)
                                                    : alpha(DARK_BLUE, 0.1)}`,
                                                }}
                                              >
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                  {msg.content}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5, gap: 0.5 }}>
                                                  <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                  >
                                                    {msg.sender_role === 'admin' ? t('admin.messageSenderAdmin') : t('admin.messageSenderClient', { id: msg.sender_id.substring(0, 8) })}
                                                    {' · '}
                                                    {new Date(msg.created_at).toLocaleString()}
                                                  </Typography>
                                                  {msg.sender_role === 'admin' && (
                                                    <Tooltip title={msg.read_by_client ? t('admin.messageRead') : t('admin.messageDelivered')}>
                                                      {msg.read_by_client ? (
                                                        <DoneAllIcon sx={{ fontSize: 14, color: GREEN }} />
                                                      ) : (
                                                        <DoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                      )}
                                                    </Tooltip>
                                                  )}
                                                </Box>
                                              </Box>
                                            ))
                                          )}
                                          <div ref={messagesEndRef} />
                                        </Box>
                                        <Stack direction="row" spacing={1}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            placeholder={t('admin.messagePlaceholder')}
                                            value={messageInput[project.id] || ''}
                                            onChange={(e) =>
                                              setMessageInput((prev) => ({
                                                ...prev,
                                                [project.id]: e.target.value,
                                              }))
                                            }
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                sendAdminMessage(project.id);
                                              }
                                            }}
                                            multiline
                                            maxRows={3}
                                            sx={{
                                              '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                            }}
                                          />
                                          <IconButton
                                            color="primary"
                                            onClick={() => sendAdminMessage(project.id)}
                                            disabled={sendingMessage || !messageInput[project.id]?.trim()}
                                            sx={{
                                              bgcolor: alpha(GREEN, 0.1),
                                              '&:hover': { bgcolor: alpha(GREEN, 0.2) },
                                            }}
                                          >
                                            {sendingMessage ? (
                                              <CircularProgress size={20} />
                                            ) : (
                                              <SendIcon />
                                            )}
                                          </IconButton>
                                        </Stack>
                                      </>
                                    )}
                                  </Box>
                                </Collapse>
                              </Box>
                            </Box>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </Stack>
          )}
        </Container>

        {/* Upload Dialog */}
        <Dialog
          open={uploadDialog.open}
          onClose={() => setUploadDialog({ open: false, projectId: '', platform: '' })}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <form onSubmit={handleUpload}>
            <DialogTitle sx={{ fontWeight: 600 }}>
              {t('admin.uploadDialogTitle', { platform: uploadDialog.platform })}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('admin.uploadDialogDesc', { platform: uploadDialog.platform })}
              </Typography>
              <input type="file" name="file" required />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setUploadDialog({ open: false, projectId: '', platform: '' })} sx={{ borderRadius: 3 }}>
                {t('admin.cancel')}
              </Button>
              <Button type="submit" variant="contained" color="secondary" sx={{ borderRadius: 3 }}>
                {t('admin.upload')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* URL Dialog */}
        <Dialog
          open={urlDialog.open}
          onClose={() => setUrlDialog({ open: false, projectId: '', url: '' })}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>{t('admin.urlDialogTitle')}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label={t('admin.urlLabel')}
              value={urlDialog.url}
              onChange={(e) => setUrlDialog((prev) => ({ ...prev, url: e.target.value }))}
              placeholder="https://..."
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setUrlDialog({ open: false, projectId: '', url: '' })} sx={{ borderRadius: 3 }}>{t('admin.cancel')}</Button>
            <Button onClick={handleUrlUpdate} variant="contained" color="secondary" sx={{ borderRadius: 3 }}>
              {t('admin.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Signature Dialog */}
        <Dialog
          open={!!signingQuoteId}
          onClose={() => setSigningQuoteId(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>{t('admin.signatureTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('admin.signatureDesc')}
            </Typography>
            <Box
              sx={{
                border: `2px solid ${alpha(DARK_BLUE, 0.15)}`,
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'crosshair',
              }}
            >
              <canvas
                ref={signatureCanvasRef}
                width={460}
                height={180}
                onMouseDown={handleSignatureMouseDown}
                onMouseMove={handleSignatureMouseMove}
                onMouseUp={handleSignatureMouseUp}
                onMouseLeave={handleSignatureMouseUp}
                style={{ display: 'block', width: '100%', height: 180 }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => initSignatureCanvas(signatureCanvasRef.current)}
              sx={{ borderRadius: 3 }}
            >
              {t('admin.clearSignature')}
            </Button>
            <Button onClick={() => setSigningQuoteId(null)} sx={{ borderRadius: 3 }}>
              {t('admin.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<DrawIcon />}
              onClick={() => signingQuoteId && handleAdminSign(signingQuoteId)}
              sx={{ borderRadius: 3 }}
            >
              {t('admin.confirmSign')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Archive Confirmation Dialog */}
        <Dialog
          open={archiveDialog.open}
          onClose={() => setArchiveDialog({ open: false, projectId: '', appName: '' })}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>{t('admin.archiveDialogTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {t('admin.archiveDialogDesc', { name: archiveDialog.appName })}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setArchiveDialog({ open: false, projectId: '', appName: '' })} sx={{ borderRadius: 3 }}>
              {t('admin.cancel')}
            </Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={<ArchiveIcon />}
              onClick={() => handleArchive(archiveDialog.projectId)}
              sx={{ borderRadius: 3 }}
            >
              {t('admin.confirmArchive')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, projectId: '', appName: '' })}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>{t('admin.deleteDialogTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {t('admin.deleteDialogDesc', { name: deleteDialog.appName })}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteDialog({ open: false, projectId: '', appName: '' })} sx={{ borderRadius: 3 }}>
              {t('admin.cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={() => handleDelete(deleteDialog.projectId)}
              sx={{ borderRadius: 3 }}
            >
              {t('admin.confirmDelete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageTransition>
  );
}
